import { create, el, awaitRedraw } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import { IButtonConfig, IButtonHandlersMap, IButtonProps, ItemEvent } from "../types";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { isEmptyObj } from "@dhx/ts-common/core";
import { baseProps } from "../helper";

export class Button extends View {
	public config: IButtonConfig;
	public events: IEventSystem<ItemEvent, IButtonHandlersMap>;
	private _handlers: IHandlers;
	private _propsItem = [
		"submit",
		"url",
		"text",
		"icon",
		"view",
		"size",
		"color",
		"full",
		"circle",
		"loading",
	];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container: HTMLElement | string, config) {
		super(container, {
			disabled: false,
			hidden: false,
			submit: false,
			full: false,
			circle: false,
			loading: false,
			view: "flat",
			size: "medium",
			color: "primary",
			width: "content",
			height: "content",
			url: "",
			text: "",
			icon: "",
			...config,
		});
		this.config.text = this.config.text || this.config.value || "";
		this.events = new EventSystem();
		this._handlers = {
			onclick: (event: Event) => this.events.fire(ItemEvent.click, [event]),
			onblur: () => this.events.fire(ItemEvent.blur, [this.config.text]),
			onfocus: () => this.events.fire(ItemEvent.focus, [this.config.text]),
			onkeydown: (event: KeyboardEvent) => this.events.fire(ItemEvent.keydown, [event]),
		};

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor() {
		this.events && this.events.clear();
		this.config = this._propsItem = this._props = this.events = this._handlers = this._uid = null;
		this.unmount();
	}
	setProperties(propertyConfig: IButtonProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.config.text = this.config.text || this.config.value;
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): IButtonProps {
		const obj: IButtonProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { text, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [text])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [text]);
	}
	hide(init?: boolean): void {
		const { text, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [text, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [text, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.button.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.button.el.blur();
		});
	}
	protected _draw() {
		const {
			color,
			size,
			view,
			full,
			loading,
			circle,
			icon,
			text,
			disabled,
			submit,
			id,
			name,
		} = this.config;
		const colorsCss =
			{
				danger: " dhx_button--color_danger",
				secondary: " dhx_button--color_secondary",
				primary: " dhx_button--color_primary",
				success: " dhx_button--color_success",
			}[color] || " dhx_button--color_primary";
		const sizeCss =
			{
				small: " dhx_button--size_small",
				medium: " dhx_button--size_medium",
			}[size] || " dhx_button--size_medium";
		const viewCss =
			{
				flat: " dhx_button--view_flat",
				link: " dhx_button--view_link",
			}[view] || " dhx_button--view_flat";
		const fullCss = full ? " dhx_button--width_full" : "";
		const circleCss = circle ? " dhx_button--circle" : "";
		const loadingCss = loading ? " dhx_button--loading" : "";
		const iconViewCss = icon && !text ? " dhx_button--icon" : "";
		return el(
			"button",
			{
				disabled,
				id,
				"data-dhx-id": name || id,
				onclick: this._handlers.onclick,
				onfocus: this._handlers.onfocus,
				onblur: this._handlers.onblur,
				onkeydown: this._handlers.onkeydown,
				type: submit ? "submit" : "button",
				class:
					"dhx_button" +
					colorsCss +
					sizeCss +
					viewCss +
					fullCss +
					circleCss +
					loadingCss +
					iconViewCss,
				_ref: "button",
			},
			[
				icon &&
					el("span.dhx_button__icon", {
						class: icon,
					}),
				text && el("span.dhx_button__text", text),
				loading &&
					el("span.dhx_button__loading", [el("span.dhx_button__loading-icon.dxi.dxi-loading")]),
			]
		);
	}
}
